import { useRef, useState } from "react";
import { FileSpreadsheet, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { parseUserFile, type ParsedFile } from "@/lib/file-parsing";
import { RawPreviewTable } from "./RawPreviewTable";

interface Props {
  parsed: ParsedFile | null;
  onChange: (parsed: ParsedFile | null) => void;
}

export const SheetCapture = ({ parsed, onChange }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      toast.error("Please upload a .csv, .xlsx or .xls file");
      return;
    }
    setBusy(true);
    try {
      const result = await parseUserFile(file);
      if (!result.rows?.length) throw new Error("No rows found in that file");
      onChange(result);
      toast.success(`Loaded ${result.rows.length} rows from ${file.name}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not read that file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        className="cursor-pointer rounded-lg border-2 border-dashed border-muted-foreground/30 p-6 text-center transition-colors hover:border-primary/50"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <div className="flex flex-col items-center gap-2">
          {busy ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />}
          <p className="text-sm font-medium">{parsed?.fileName ?? "Drop a spreadsheet or click to upload"}</p>
          <p className="text-xs text-muted-foreground">Accepted formats: CSV, Excel (.xlsx, .xls)</p>
        </div>
      </div>

      {parsed?.rows?.length ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Raw preview — {parsed.rows.length} rows, {parsed.columns?.length ?? 0} columns
            </p>
            <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
              <X className="mr-1 h-3 w-3" /> Clear
            </Button>
          </div>
          <RawPreviewTable columns={parsed.columns ?? []} rows={parsed.rows.slice(0, 20)} />
        </div>
      ) : null}
    </div>
  );
};
